import React, { useState } from 'react';
import { Crown, Calendar, CreditCard, AlertCircle, CheckCircle, Clock, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { stripeService, type SubscriptionData } from '@/services/stripe-service';
import { Modal, ModalFooter } from '@/components/ui/Modal';

interface SubscriptionCardProps {
  subscription: SubscriptionData | null;
  onManage?: () => void;
  onUpgrade?: () => void;
  loading?: boolean;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onManage,
  onUpgrade,
  loading = false
}) => {
  const isActive = subscription && ['active', 'trialing'].includes(subscription?.subscription_status || '');
  const isCanceled = subscription?.subscription_status === 'cancelled' || subscription?.subscription_status === 'canceled';
  const isPastDue = subscription?.subscription_status === 'past_due';
  const willCancel = false; // Cette information n'est plus disponible dans la table profiles
  
  const statusDisplay = subscription 
    ? stripeService.getSubscriptionStatusDisplay(subscription.subscription_status)
    : { label: 'Aucun abonnement', color: 'gray' };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-10 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subscription || !subscription.subscription_id) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <Crown className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun abonnement actif</h3>
            <p className="text-gray-600 mb-6">
              Souscrivez à un abonnement pour accéder aux analyses illimitées et aux fonctionnalités premium.
            </p>
            <Button 
              variant="primary"
              onClick={onUpgrade}
              className="w-full"
            >
              Voir les abonnements
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Crown className={`w-6 h-6 ${isActive ? 'text-yellow-500' : 'text-gray-400'}`} />
            <div>
              <CardTitle>Abonnement DPEWizard</CardTitle>
              <CardDescription>
                Plan Professionnel
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Statut</span>
            <div className="flex items-center gap-2">
              {isActive ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : isPastDue ? (
                <AlertCircle className="w-4 h-4 text-orange-500" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
              <span className={`font-medium ${
                statusDisplay.color === 'green' ? 'text-green-600' :
                statusDisplay.color === 'blue' ? 'text-blue-600' :
                statusDisplay.color === 'orange' ? 'text-orange-600' :
                statusDisplay.color === 'red' ? 'text-red-600' :
                'text-gray-600'
              }`}>
                {statusDisplay.label}
              </span>
            </div>
          </div>

          {subscription?.current_period_end && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">
                {willCancel ? 'Se termine le' : 'Prochaine facturation'}
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="font-medium">
                  {stripeService.formatDate(subscription.current_period_end)}
                </span>
              </div>
            </div>
          )}

          {willCancel && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Votre abonnement sera annulé le {stripeService.formatDate(subscription.current_period_end!)}
                </span>
              </div>
            </div>
          )}

          {isActive && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Abonnement actif</span>
              </div>
              <ul className="text-sm text-green-600 space-y-1">
                <li>• Analyses DPE illimitées</li>
                <li>• Questions contextuelles avancées</li>
                <li>• Recommandations personnalisées</li>
                <li>• Support prioritaire</li>
              </ul>
            </div>
          )}

          {isPastDue && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Problème de paiement détecté. Veuillez mettre à jour votre moyen de paiement.
                </span>
              </div>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={onManage}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Gérer l'abonnement
          </Button>
          
          {isActive && !willCancel && (
            <Button
              variant="outline" 
              className="w-full"
              onClick={onManage}
            >
              Annuler l'abonnement
            </Button>
          )}
          
          {willCancel && (
            <Button
              variant="outline"
              className="w-full text-green-600 hover:bg-green-50 hover:text-green-700 border-green-200"
              onClick={onManage}
            >
              Réactiver l'abonnement
            </Button>
          )}
        </CardFooter>
      </Card>
    </>
  );
};

export default SubscriptionCard;